import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useComprehensiveReports, useLandlords, useLandlordProperties } from "services/hooks/rentals";
import * as XLSX from "xlsx";
import { months, years, formatCurrency } from "./constants";

const buildExcelAndDownload = (reportData, summary, filters, landlords, landlordProperties, formatCurrencyFn) => {
  const data = Array.isArray(reportData) ? reportData : [];
  const summ = summary ?? null;
  const landlordName = filters.landlord_id
    ? (landlords?.find((l) => l.id === Number(filters.landlord_id))?.business_name ?? `Landlord #${filters.landlord_id}`)
    : "All Landlords";
  const propertyName = filters.rental_id
    ? (landlordProperties?.find((p) => p.id === Number(filters.rental_id))?.rental_name ?? `Property #${filters.rental_id}`)
    : "All Properties";

  const rows = [
    ["Comprehensive Report"],
    [],
    ["Report parameters"],
    ["Landlord", "Property", "Month", "Year", "Include Unoccupied"],
    [landlordName, propertyName, filters.month || "All Months", filters.year || "All Years", filters.include_unpaid ? "Yes" : "No"],
    [],
    ["Summary"],
    ["Total Units", "Occupied", "Unoccupied", "Total Rent Amount", "Total Paid", "Outstanding", "Management Fees"],
  ];

  if (summ) {
    rows.push([
      summ.total_units ?? "",
      summ.occupied_units ?? "",
      summ.unoccupied_units ?? "",
      formatCurrencyFn(summ.total_rent_amount),
      formatCurrencyFn(summ.total_paid_amount),
      formatCurrencyFn(summ.total_outstanding),
      formatCurrencyFn(summ.total_management_fees),
    ]);
  } else {
    rows.push(["", "", "", "", "", "", ""]);
  }

  rows.push([]);
  rows.push([
    "Transaction ID", "Transaction Date", "Tenant Name", "Tenant Contact",
    "Property Name / Description", "Unit Number", "Landlord Name", "Monthly Rent Amount",
    "Amount Paid", "Month Paid For", "Outstanding Amount", "Management Fee", "Payment Status", "Payment Method"
  ]);

  data.forEach((row) => {
    rows.push([
      row["Transaction ID"],
      row["Transaction Date"],
      row["Tenant Name"],
      row["Tenant Contact"],
      row["Property Name / Description"],
      row["Unit Number"],
      row["Landlord Name"],
      typeof row["Monthly Rent Amount"] === "number" ? formatCurrencyFn(row["Monthly Rent Amount"]) : (row["Monthly Rent Amount"] ?? ""),
      typeof row["Amount Paid"] === "number" ? formatCurrencyFn(row["Amount Paid"]) : (row["Amount Paid"] ?? ""),
      row["Month Paid For"],
      typeof row["Outstanding Amount"] === "number" ? formatCurrencyFn(row["Outstanding Amount"]) : (row["Outstanding Amount"] ?? ""),
      typeof row["Management Fee"] === "number" ? formatCurrencyFn(row["Management Fee"]) : (row["Management Fee"] ?? ""),
      row["Payment Status"],
      row["Payment Method"],
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Comprehensive Report");
  const fileName = `comprehensive-report-${filters.landlord_id || "all"}-${filters.month || "all"}-${filters.year || "all"}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

const getPaymentStatusColor = (status) => {
  switch (status) {
    case "Paid": return "success";
    case "Partially Paid": return "warning";
    case "Unpaid": return "error";
    default: return "default";
  }
};

const ComprehensiveReport = () => {
  const [filters, setFilters] = useState({
    landlord_id: "",
    rental_id: "",
    month: "",
    year: "",
    include_unpaid: true,
  });

  const { reportData, summary, isLoadingReports, error } = useComprehensiveReports(filters);
  const { landlords } = useLandlords();
  const { landlordProperties, loading: loadingProperties } = useLandlordProperties(filters.landlord_id || 0);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "landlord_id") next.rental_id = "";
      return next;
    });
  }, []);

  const handleDownloadExcel = () => {
    buildExcelAndDownload(reportData, summary, filters, landlords, landlordProperties, formatCurrency);
  };

  const propertyOptions = filters.landlord_id ? (landlordProperties || []) : [];
  const canDownloadExcel = summary !== null || (reportData && reportData.length > 0);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Report Filters
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose &quot;All Landlords&quot; or a specific landlord; report data updates automatically. When a landlord is selected, the Property dropdown shows only that landlord&apos;s properties. Use &quot;Download Excel&quot; to export the current view.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Landlord</InputLabel>
              <Select value={filters.landlord_id} onChange={(e) => handleFilterChange("landlord_id", e.target.value)} label="Landlord">
                <MenuItem value="">All Landlords</MenuItem>
                {(landlords || []).map((landlord) => (
                  <MenuItem key={landlord.id} value={landlord.id}>
                    {landlord.business_name ?? landlord.rental_name ?? `Landlord #${landlord.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth disabled={!filters.landlord_id}>
              <InputLabel>Property</InputLabel>
              <Select value={filters.rental_id} onChange={(e) => handleFilterChange("rental_id", e.target.value)} label="Property">
                <MenuItem value="">All Properties</MenuItem>
                {propertyOptions.map((rental) => (
                  <MenuItem key={rental.id} value={rental.id}>
                    {rental.rental_name ?? rental.name ?? `Property #${rental.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {filters.landlord_id && loadingProperties && (
              <Typography variant="caption" color="text.secondary">Loading properties…</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select value={filters.month} onChange={(e) => handleFilterChange("month", e.target.value)} label="Month">
                <MenuItem value="">All Months</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>{month}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select value={filters.year} onChange={(e) => handleFilterChange("year", e.target.value)} label="Year">
                <MenuItem value="">All Years</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={<Switch checked={filters.include_unpaid} onChange={(e) => handleFilterChange("include_unpaid", e.target.checked)} />}
              label="Include Unoccupied"
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleDownloadExcel} disabled={!canDownloadExcel} sx={{ mt: 1 }}>
              Download Excel
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
              Report updates when you change filters. Download Excel to export the data currently on screen.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {isLoadingReports && (
        <Alert severity="info" sx={{ mb: 2 }}>Fetching records…</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>Error loading reports. Please try again.</Alert>
      )}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Units</Typography>
                <Typography variant="h4">{summary.total_units}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Occupied: {summary.occupied_units} | Unoccupied: {summary.unoccupied_units}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Rent Amount</Typography>
                <Typography variant="h4">{formatCurrency(summary.total_rent_amount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Paid</Typography>
                <Typography variant="h4" color="success.main">{formatCurrency(summary.total_paid_amount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Outstanding</Typography>
                <Typography variant="h4" color="error.main">{formatCurrency(summary.total_outstanding)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Management Fees</Typography>
                <Typography variant="h4">{formatCurrency(summary.total_management_fees)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {reportData && reportData.length > 0 && (
        <Paper sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ p: 2 }}>Report Data ({reportData.length} transactions)</Typography>
          <Divider />
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Landlord</TableCell>
                  <TableCell>Rent Amount</TableCell>
                  <TableCell>Amount Paid</TableCell>
                  <TableCell>Month Paid For</TableCell>
                  <TableCell>Outstanding</TableCell>
                  <TableCell>Management Fee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...reportData]
                  .sort((a, b) => {
                    const da = new Date(a["Transaction Date"] || 0);
                    const db = new Date(b["Transaction Date"] || 0);
                    if (isNaN(db.getTime())) return -1;
                    if (isNaN(da.getTime())) return 1;
                    return db - da;
                  })
                  .map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{row["Transaction ID"]}</TableCell>
                      <TableCell>{row["Transaction Date"]}</TableCell>
                      <TableCell>{row["Tenant Name"]}</TableCell>
                      <TableCell>{row["Tenant Contact"]}</TableCell>
                      <TableCell>{row["Property Name / Description"]}</TableCell>
                      <TableCell>{row["Unit Number"]}</TableCell>
                      <TableCell>{row["Landlord Name"]}</TableCell>
                      <TableCell>{formatCurrency(row["Monthly Rent Amount"])}</TableCell>
                      <TableCell>{formatCurrency(row["Amount Paid"])}</TableCell>
                      <TableCell>{row["Month Paid For"]}</TableCell>
                      <TableCell>{formatCurrency(row["Outstanding Amount"])}</TableCell>
                      <TableCell>{formatCurrency(row["Management Fee"])}</TableCell>
                      <TableCell>
                        <Chip label={row["Payment Status"]} color={getPaymentStatusColor(row["Payment Status"])} size="small" />
                      </TableCell>
                      <TableCell>{row["Payment Method"]}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {reportData && reportData.length === 0 && !isLoadingReports && summary && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">No transactions for the selected filters</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ComprehensiveReport;
